import React from "react";
import { Button, Typography, IconButton, Tooltip, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ArticleIcon from "@mui/icons-material/Article";
import AddIcon from "@mui/icons-material/Add";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";

// pickFirst is used in multiple columns, so it's included here.
const pickFirst = (obj, keys = []) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

// Extracted from the inline component for reusability and performance.
const ChildrenCountCell = ({ row, onShow, active, onAddChild }) => {
  const count = row?._childCount || 0;
  const label = count ? `${count}` : "—";
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Button
        size="small"
        onClick={() => (count ? onShow?.(row) : onAddChild?.(row))}
        color={active ? "primary" : "inherit"}
        variant={active ? "contained" : "text"}
        disabled={!count}
      >
        {label}
      </Button>
      <Tooltip title="Add children for this name">
        <IconButton size="small" onClick={() => onAddChild?.(row)}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export const getColumns = ({
  onEdit,
  onEditDocs,
  onShowChildrenInline,
  activeChildFilterParentId,
  onAddChild,
  onDeleteTree,
  onBulkMoveChildren,
  canDeleteTree,
}) => {
  const cols = [
    {
      field: "_childCount",
      headerName: "Has Children?",
      width: 120,
      sortable: false,
      renderCell: (p) => (
        <ChildrenCountCell
          row={p?.row}
          onShow={onShowChildrenInline}
          active={activeChildFilterParentId === p?.row?.id}
          onAddChild={onAddChild}
        />
      ),
    },
    { field: "current_rank", headerName: "Current Rank", width: 120 },
    {
      field: "current_parent",
      headerName: "Current Parent",
      width: 200,
      renderCell: (p) => (
        <Typography variant="body2">
          {p?.row?._currentParentName || p?.row?.current_parent || ""}
        </Typography>
      ),
    },
    {
      field: "name_spell_valid",
      headerName: "Current Spelling",
      minWidth: 220,
      flex: 1,
      renderCell: (p) => <Typography variant="body2">{p?.row?._nameValidDisp || "—"}</Typography>,
    },
    {
      field: "name_spell_original",
      headerName: "Original Spelling",
      minWidth: 200,
      flex: 1,
      renderCell: (p) => <Typography variant="body2">{p?.row?._nameOrigDisp || "—"}</Typography>,
    },
    {
      field: "authority",
      headerName: "Authority",
      minWidth: 200,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {p?.row?._authors || "—"}
        </Typography>
      ),
      sortable: false,
    },
    { field: "authority_year", headerName: "Year", width: 90 },
    {
      field: "original_rank",
      headerName: "Original Rank",
      width: 120,
      renderCell: (p) => (
        <Typography variant="body2">
          {pickFirst(p?.row, ["original_rank", "originalRank"]) || "—"}
        </Typography>
      ),
    },
    {
      field: "original_parent",
      headerName: "Original Parent",
      width: 200,
      renderCell: (p) => (
        <Typography variant="body2">
          {p?.row?._originalParentName || p?.row?.original_parent || "—"}
        </Typography>
      ),
    },
    {
      field: "type_taxa_id",
      headerName: "Type Taxa ID",
      width: 150,
      renderCell: (p) => (
        <Typography variant="body2">
          {pickFirst(p?.row, ["type_taxa_id", "typeTaxaId"]) || "—"}
        </Typography>
      ),
    },
    {
      field: "extant_fossil",
      headerName: "Extant/Fossil",
      width: 120,
      renderCell: (p) => {
        const val = pickFirst(p?.row, ["extant_fossil", "extantFossil"]);
        if (val === null || val === undefined || val === "") return <Typography variant="body2">—</Typography>;
        return <Typography variant="body2">{String(val)}</Typography>;
      },
    },
    {
      field: "type_category",
      headerName: "Type Category",
      width: 140,
      renderCell: (p) => (
        <Typography variant="body2">
          {pickFirst(p?.row, ["type_category", "typeCategory"]) || "—"}
        </Typography>
      ),
    },
    {
      field: "type_locality",
      headerName: "Type Locality",
      width: 160,
      renderCell: (p) => (
        <Typography variant="body2">
          {pickFirst(p?.row, ["type_locality", "typeLocality"]) || "—"}
        </Typography>
      ),
    },
    {
      field: "type_sex",
      headerName: "Type Sex",
      width: 100,
      renderCell: (p) => (
        <Typography variant="body2">
          {pickFirst(p?.row, ["type_sex", "typeSex"]) || "—"}
        </Typography>
      ),
    },
    {
      field: "type_repository",
      headerName: "Type Repository",
      minWidth: 240,
      flex: 1,
      renderCell: (p) => <Typography variant="body2">{p?.row?._typeRepo || "—"}</Typography>,
    },
    {
      field: "type_host",
      headerName: "Type Host",
      width: 160,
      renderCell: (p) => (
        <Typography variant="body2">
          {pickFirst(p?.row, ["type_host", "typeHost"]) || "—"}
        </Typography>
      ),
    },
    {
      field: "source_of_original_description",
      headerName: "Source",
      width: 180,
      renderCell: (p) => (
        <Typography variant="body2">
          {pickFirst(p?.row, ["source_of_original_description", "sourceOfOriginalDescription"]) || "—"}
        </Typography>
      ),
    },
    {
      field: "page",
      headerName: "Page",
      width: 80,
      renderCell: (p) => (
        <Typography variant="body2">
          {pickFirst(p?.row, ["page"]) || "—"}
        </Typography>
      ),
    },
    {
      field: "remark",
      headerName: "Remark",
      minWidth: 220,
      flex: 1,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {p?.row?._remark || "—"}
        </Typography>
      ),
    },
    {
      field: "last_update",
      headerName: "Last Update",
      width: 160,
      renderCell: (p) => <Typography variant="body2">{p?.row?._lastUpdateDisp || "—"}</Typography>,
    },
  ];
  cols.push({
    field: "__actions",
    headerName: "Actions",
    width: 140,
    sortable: false,
    filterable: false,
    renderCell: (p) =>
      p?.row ? (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit details">
            <IconButton size="small" onClick={() => onEdit?.(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit notes (overview / diagnosis)">
            <IconButton size="small" onClick={() => onEditDocs?.(p.row)}>
              <ArticleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {(p?.row?._childCount || 0) > 0 && (
            <Tooltip title="Move children to another parent">
              <IconButton
                size="small"
                onClick={() => onBulkMoveChildren?.(p.row)}
              >
                <DriveFileMoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDeleteTree && (
            <Tooltip title={(p?.row?._childCount || 0) > 0 ? "Delete subtree" : "No children"}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={(p?.row?._childCount || 0) === 0}
                  onClick={() => onDeleteTree?.(p.row)}
                >
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      ) : null,
    });
  return cols;
};
